import {type ChannelStatus} from '@/api/channels';
import {type VideoStatus} from '@/api/videos';

/**
 Scrapy's marker for "we looked and there was nothing". Emitted rather than omitting the field, so
 every consumer has to treat it as absent - see `itemMetadata.ts`, which does the same.
 */
const ABSENT = 'Not specified';

/**
 ISO 8601 duration ("PT1H2M3S", the raw shape YouTube's Data API returns) to "HH:MM:SS", matching
 Scrapy's own convention for this field - DurationBadge renders the stored string verbatim with no
 parsing, so sending the raw ISO form would show literally "PT1M12S" on the thumbnail overlay.
 */
export function formatDuration(iso8601: string | null | undefined): string | null {
  const match = iso8601 ? /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso8601) : null;
  if (!match) {
    return null;
  }

  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2] ?? 0);
  const seconds = Number(match[3] ?? 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 YouTube returns a channel's custom URL as `@handle`. Scrapy-collected items store the bare handle
 in `Author Handle`, so the leading `@` is stripped to keep one shape across both sources.
 */
export function channelHandle(customUrl: string | null | undefined): string | null {
  const trimmed = customUrl?.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
}

/**
 The `Fields[]` blob stored on an item created from the Single Submission page. Deliberately the
 same `{FieldName, Content}` shape and the same field *names* Scrapy uses, because everything
 downstream reads the two identically - ClassifyDialog's language/location auto-select, the item
 metadata card, and the channel element of the Apollo message, which is built from these when no
 channel was ever crawled.
 */
export function buildVideoItemMetadataFields(
  video: VideoStatus
): Array<{FieldName: string; Content: string | boolean}> {
  const fields: Array<{FieldName: string; Content: string | boolean}> = [
    {FieldName: 'Default Language', Content: video.default_language ?? ABSENT},
    {FieldName: 'Default Audio Language', Content: video.default_audio_language ?? ABSENT},
    {FieldName: 'Country', Content: video.channel_country ?? ABSENT},
    // The video was just fetched successfully from the platform, which is what "live" means in
    // Apollo's own ContentStatus vocabulary (Live/Removed) - it is about availability, not
    // livestreaming. A Scrapy crawl can observe "removed" because it revisits over time; a one-shot
    // Data API lookup at submission time never can, so "live" is the only value this path can ever
    // honestly report.
    {FieldName: 'Live Status', Content: 'live'}
  ];

  if (video.description) {
    fields.push({FieldName: 'Description', Content: video.description});
  }

  if (video.tags) {
    fields.push({FieldName: 'Tags', Content: video.tags});
  }

  if (video.view_count !== null && video.view_count !== undefined) {
    fields.push({FieldName: 'View count', Content: String(video.view_count)});
  }

  if (video.like_count !== null && video.like_count !== undefined) {
    fields.push({FieldName: 'Like count', Content: String(video.like_count)});
  }

  if (video.comment_count !== null && video.comment_count !== undefined) {
    fields.push({FieldName: 'Comment count', Content: String(video.comment_count)});
  }

  if (video.category_name) {
    fields.push({FieldName: 'Category name', Content: video.category_name});
  }

  const duration = formatDuration(video.duration);
  if (duration !== null) {
    fields.push({FieldName: 'Duration', Content: duration});
  }

  if (video.region_restriction_blocked && video.region_restriction_blocked.length > 0) {
    // Exact casing matters here specifically: Apollo's YouTubeVideoFields.RestrictedRegions has no
    // case-insensitive raw-Fields fallback the way most other properties do, so this is deliberately
    // "Restricted Regions" rather than following Scrapy's own lowercase-second-word convention.
    fields.push({
      FieldName: 'Restricted Regions',
      Content: video.region_restriction_blocked.join(', ')
    });
  }

  // Sent as a real boolean, not String(...): getMetaBoolean compares `Content === true`, so a
  // stringified flag reads as absent everywhere downstream. Omitted when the platform didn't report
  // it - the field is undocumented upstream, and an absent value means unknown, not "unsafe".
  if (video.safe_for_minors !== null && video.safe_for_minors !== undefined) {
    fields.push({FieldName: 'Safe for Minors', Content: video.safe_for_minors});
  }

  // Channel-scoped values, under the names a crawled video uses for them. Omitted when unknown
  // rather than sent as the absent marker: a subscriber count of "Not specified" is worse than no
  // subscriber count, since a reader cannot tell it from a real value without knowing the marker.
  if (video.age_restricted !== null && video.age_restricted !== undefined) {
    fields.push({FieldName: 'Age restricted', Content: video.age_restricted});
  }

  if (video.is_livestream !== null && video.is_livestream !== undefined) {
    fields.push({FieldName: 'islivestream', Content: video.is_livestream});
  }

  const handle = channelHandle(video.channel_custom_url);
  if (handle !== null) {
    fields.push({FieldName: 'Author Handle', Content: handle});
  }

  // A hidden subscriber count arrives as 0 rather than null, so publishing it would send a fabricated
  // measurement downstream. Omitted instead: absent reads as unknown, which is the truth.
  if (
    video.channel_subscriber_count !== null &&
    video.channel_subscriber_count !== undefined &&
    video.channel_hidden_subscriber_count !== true
  ) {
    fields.push({FieldName: 'Subscribers', Content: String(video.channel_subscriber_count)});
  }

  if (video.channel_video_count !== null && video.channel_video_count !== undefined) {
    fields.push({FieldName: 'Video count', Content: String(video.channel_video_count)});
  }

  if (video.is_channel_monetization_enabled !== null) {
    fields.push({
      FieldName: 'Is Channel Monetized',
      Content: video.is_channel_monetization_enabled
    });
  }

  return fields;
}

/**
 The channel counterpart of `buildVideoItemMetadataFields`, under the same Scrapy `FieldName`s a
 crawled channel carries (`itemMetadata.ts`'s channel-only aliases, and the names
 `ApolloSubmissionPayload` reads) - so an actor item synthesized from a live lookup is indistinguishable
 from a scraped one downstream. Without this, a channel submitted from the Single Submission page
 reached ClassifyDialog, the item card and Apollo carrying `Country` and nothing else, while a video
 from the same page carried fifteen fields.
 */
export function buildChannelItemMetadataFields(
  channel: ChannelStatus
): Array<{FieldName: string; Content: string | boolean}> {
  const fields: Array<{FieldName: string; Content: string | boolean}> = [
    {FieldName: 'Country', Content: channel.country ?? ABSENT}
  ];

  if (channel.description) {
    fields.push({FieldName: 'Description', Content: channel.description});
  }

  // Omitted rather than sent as the absent marker, for the reason buildVideoItemMetadataFields gives
  // about its own channel-scoped counts - and omitted outright when the channel hides the count,
  // which the platform reports as 0 rather than as null.
  if (
    channel.subscriber_count !== null &&
    channel.subscriber_count !== undefined &&
    channel.hidden_subscriber_count !== true
  ) {
    fields.push({FieldName: 'Subscribers', Content: String(channel.subscriber_count)});
  }

  if (channel.video_count !== null && channel.video_count !== undefined) {
    fields.push({FieldName: 'Video count', Content: String(channel.video_count)});
  }

  if (channel.view_count !== null && channel.view_count !== undefined) {
    fields.push({FieldName: 'View count', Content: String(channel.view_count)});
  }

  if (channel.is_channel_monetization_enabled !== null) {
    fields.push({
      FieldName: 'Is monetized',
      Content: channel.is_channel_monetization_enabled
    });
  }

  if (channel.made_for_kids !== null) {
    fields.push({FieldName: 'Made for kids', Content: channel.made_for_kids});
  }

  const handle = channelHandle(channel.custom_url);
  if (handle !== null) {
    fields.push({FieldName: 'Author Handle', Content: handle});
  }

  // Date-only: ItemMeta renders this field verbatim as "Joined <value>", and the channel creation
  // timestamp's time-of-day is noise nobody reads.
  if (channel.published_at) {
    fields.push({FieldName: 'Joined date', Content: channel.published_at.slice(0, 10)});
  }

  return fields;
}
